import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, ArrowLeft, Plus } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { AuthForm } from "@/components/AuthForm";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const STATUS = { new: ["Received", "text-[var(--gold)] border-[var(--gold)]/50"], in_progress: ["In progress", "text-[var(--arcane)] border-[var(--arcane)]/60"], completed: ["Completed", "text-emerald-300 border-emerald-400/50"] };
const FIELDS = [["name", "Name", true], ["phone", "Phone"], ["business", "Business / brand"], ["social", "Instagram, TikTok or website"]];

const Profile = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState(() => Object.fromEntries(["name", "phone", "business", "social", "bio"].map((k) => [k, user?.[k] ?? ""])));
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || (k === "name" ? v : null)]));
      const { data } = await api.put("/auth/profile", body);
      setUser(data);
      toast.success("Profile saved");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="profile-form" className="border border-[var(--line)] bg-[var(--crypt)] p-7 sm:p-9 space-y-4">
      <div className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--gold)]">Your profile</div>
      <div className="font-mono text-sm text-[var(--ink-dim)] break-all" data-testid="profile-email">{user?.email}</div>
      {FIELDS.map(([k, l, req]) => (
        <label key={k} className="block">
          <span className="text-xs text-[var(--ink-dim)]">{l}</span>
          <input required={req} data-testid={`profile-${k}-input`} className="field mt-1" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
        </label>
      ))}
      <label className="block">
        <span className="text-xs text-[var(--ink-dim)]">About you or your brand</span>
        <textarea rows={4} data-testid="profile-bio-input" className="field mt-1 resize-none" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} />
      </label>
      <button disabled={busy} data-testid="profile-save-btn" className="btn-blood w-full py-4 font-mono text-sm tracking-[0.2em] uppercase disabled:opacity-60">
        {busy ? "Saving…" : "Save profile"}
      </button>
    </form>
  );
};

const MyRequests = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/my/requests").then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
  }, []);
  return (
    <div data-testid="my-requests">
      <div className="flex items-center justify-between">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--gold)]">Your requests</div>
        <Link to="/#contact" data-testid="new-request-link" className="btn-ghost px-4 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><Plus className="w-4 h-4" /> New request</Link>
      </div>
      <div className="mt-6 space-y-4">
        {items === null && <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" />}
        {items?.length === 0 && (
          <p data-testid="my-requests-empty" className="text-[var(--ink-dim)] border border-dashed border-[var(--line)] p-8 text-center">
            No requests yet. Send one from the contact form while logged in and it will show up here.
          </p>
        )}
        {(items || []).map((s) => {
          const [label, cls] = STATUS[s.status] || STATUS.new;
          return (
            <article key={s.id} data-testid={`my-request-${s.id}`} className="border border-[var(--line)] bg-[var(--crypt)] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-display text-xl font-bold capitalize">{s.service}</span>
                <span className={`border px-3 py-1 font-mono text-[11px] uppercase tracking-widest ${cls}`}>{label}</span>
              </div>
              <div className="font-mono text-xs text-[var(--ink-dim)] mt-2">{new Date(s.created_at).toLocaleString()}</div>
              <p className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">{s.details}</p>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default function Account() {
  const { user, logout } = useAuth();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main data-testid="account-page" className="min-h-screen relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[520px] h-[520px] rounded-full bg-[var(--blood)]/15 blur-[140px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-10 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="account-home-link"><Wordmark /></Link>
          {user && <button data-testid="account-logout-btn" onClick={logout} className="btn-ghost px-4 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>}
        </div>
        {user === null && <div className="grid place-items-center py-40"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>}
        {user === false && (
          <div className="grid place-items-center py-16 sm:py-24">
            <AuthForm />
            <Link to="/" data-testid="account-back-link" className="mt-8 flex items-center gap-2 text-sm text-[var(--ink-dim)] hover:text-[var(--gold)]"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
          </div>
        )}
        {user && (
          <>
            <Reveal className="mt-14">
              <div className="font-mono text-xs tracking-[0.35em] uppercase text-[var(--blood)]">Your account</div>
              <h1 data-testid="account-greeting" className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-4">
                Hail, <span className="text-gilded italic">{user.name || "friend"}</span>.
              </h1>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="account-admin-link" className="inline-block mt-6 btn-ghost px-5 py-3 font-mono text-xs uppercase tracking-widest">Open admin dashboard</Link>
              )}
            </Reveal>
            <div className="mt-12 grid lg:grid-cols-12 gap-8 pb-20">
              <Reveal className="lg:col-span-5"><Profile /></Reveal>
              <Reveal delay={0.1} className="lg:col-span-7"><MyRequests /></Reveal>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
